import Phaser from "phaser";
import { AssetKeys } from "../assets";
import { GAME_DURATION, GAME_HEIGHT, GAME_WIDTH, GHOST_FRONT_LINE, PLAYER_TARGET_Y, TERRAIN, TerrainType } from "../constants";
import type { GamePhase, ObstacleData, PickupData } from "../types";
import { AudioSyncSystem } from "../systems/AudioSyncSystem";
import { ChampionPulseSystem } from "../systems/ChampionPulseSystem";
import { CollisionSystem } from "../systems/CollisionSystem";
import { EnergyPickupFeedbackSystem, type EnergyPickupVisual } from "../systems/EnergyPickupFeedbackSystem";
import { EnvironmentalStoryBeatSystem } from "../systems/EnvironmentalStoryBeatSystem";
import { GhostWallSystem } from "../systems/GhostWallSystem";
import { MapSystem } from "../systems/MapSystem";
import { MicrotextSystem, type MicrotextMessage } from "../systems/MicrotextSystem";
import { PlayerController } from "../systems/PlayerController";
import { RhythmRewardSystem } from "../systems/RhythmRewardSystem";
import { RunRewardSystem } from "../systems/RunRewardSystem";
import { ScoreSystem } from "../systems/ScoreSystem";
import { SongEnergySystem } from "../systems/SongEnergySystem";
import { SongSyncCollectibleSystem } from "../systems/SongSyncCollectibleSystem";
import { SpiritPressureSystem } from "../systems/SpiritPressureSystem";
import { WolfStaminaSystem, type WolfMovementFeel } from "../systems/WolfStaminaSystem";
import { WorldLifeSystem } from "../systems/WorldLifeSystem";
import { WOLF_FEEL_CONFIG } from "../wolfFeelConfig";
import { showCrashOverlay } from "../debug/CrashReporter";

type ObstacleVisual = { sprite: Phaser.GameObjects.Image; shadow: Phaser.GameObjects.Ellipse };
export class RunScene extends Phaser.Scene {
  private phase: GamePhase = "start";
  private audio = new AudioSyncSystem();
  private map = new MapSystem();
  private collision = new CollisionSystem();
  private score = new ScoreSystem();
  private songEnergy = new SongEnergySystem();
  private songCollectibles = new SongSyncCollectibleSystem();
  private wolfStamina = new WolfStaminaSystem();
  private worldLife!: WorldLifeSystem;
  private energyFeedback!: EnergyPickupFeedbackSystem;
  private spiritPressure!: SpiritPressureSystem;
  private championPulse!: ChampionPulseSystem;
  private rhythmReward!: RhythmRewardSystem;
  private runRewards = new RunRewardSystem();
  private storyBeats = new EnvironmentalStoryBeatSystem();
  private microtext = new MicrotextSystem();
  private player!: PlayerController;
  private ghosts!: GhostWallSystem;
  private debug = false;
  private worldSpeed = 220;
  private distance = 0;
  private rearThreat = 0;
  private noForwardTime = 0;
  private capturePressureTime = 0;
  private danger = 0;
  private fps = 60;
  private muted = false;
  private ground!: Phaser.GameObjects.TileSprite;
  private debugGraphics!: Phaser.GameObjects.Graphics;
  private dangerBorder!: Phaser.GameObjects.Graphics;
  private vignette!: Phaser.GameObjects.Graphics;
  private layers!: {
    terrain: Phaser.GameObjects.Layer;
    shadows: Phaser.GameObjects.Layer;
    obstacles: Phaser.GameObjects.Layer;
    pickups: Phaser.GameObjects.Layer;
    actors: Phaser.GameObjects.Layer;
    fog: Phaser.GameObjects.Layer;
    particles: Phaser.GameObjects.Layer;
    debug: Phaser.GameObjects.Layer;
  };
  private obstacleSprites = new Map<string, ObstacleVisual>();
  private pickupSprites = new Map<string, EnergyPickupVisual>();
  private shakeCooldown = 0;
  private hitRegisterCooldown = 0;
  private cameraScrollY = 0;
  private cameraTargetScrollY = 0;
  private cameraScrollDeltaY = 0;
  private movementFeel: WolfMovementFeel = this.wolfStamina.currentFeel;
  private crashed = false;

  constructor() {
    super("RunScene");
  }

  create() {
    this.debug = new URLSearchParams(window.location.search).get("debug") === "1";
    this.createLayers();
    this.worldLife = new WorldLifeSystem(this, this.layers);
    this.energyFeedback = new EnergyPickupFeedbackSystem(this, this.layers);
    this.spiritPressure = new SpiritPressureSystem(this, this.layers);
    this.championPulse = new ChampionPulseSystem(this, this.layers);
    this.rhythmReward = new RhythmRewardSystem(this, this.layers.actors);
    this.map.reset();
    this.player = new PlayerController(this, this.layers.actors);
    this.ghosts = new GhostWallSystem(this, this.layers.actors);
    this.debugGraphics = this.add.graphics();
    this.layers.debug.add(this.debugGraphics);
    this.layers.debug.setVisible(this.debug);

    this.game.events.on("ui:start", () => this.startRun());
    this.game.events.on("game:pause-toggle", () => this.togglePause());
    this.game.events.on("game:restart", () => this.restartFromPause());
    this.game.events.on("game:home", () => this.returnToStart());
    this.game.events.on("debug:toggle", () => this.toggleDebug());
    this.game.events.on("audio:mute-toggle", () => {
      this.muted = !this.muted;
      this.audio.setMuted(this.muted);
    });
    // Auto-pause when the page is hidden (screen lock, app switch, tab change).
    // Reuses the existing pause flow (audio + wolf + tweens + pause panel) and is
    // pause-only: guarded on phase === "playing" so it never toggles back to
    // playing, never re-pauses, and never disturbs start/gameover/victory. On
    // return the game stays paused; the player resumes manually with CONTINUAR.
    // Registered once (create() is not re-run between runs), so no duplicate hooks.
    document.addEventListener("visibilitychange", () => {
      if (document.hidden && this.phase === "playing") this.togglePause();
    });
    this.game.events.emit("ui:show-start");
  }

  update(_: number, deltaMs: number) {
    const dt = Math.min(1 / 24, deltaMs / 1000);
    this.fps = Phaser.Math.Linear(this.fps, 1000 / Math.max(deltaMs, 1), 0.08);
    if (this.phase === "playing") {
      // PASO 1 instrumentation: an uncaught exception here would otherwise break
      // Phaser's requestAnimationFrame loop and freeze the game forever (the
      // mobile-only freeze). Catch it, surface the full stack (console + on-screen
      // panel readable on the device), and degrade cleanly instead of freezing.
      try {
        this.updatePlaying(dt);
      } catch (error) {
        this.handleUpdateCrash(error);
      }
    }
    this.updateAmbient(dt);
    this.emitHud();
  }

  private handleUpdateCrash(error: unknown) {
    showCrashOverlay("RunScene.updatePlaying", error);
    if (this.crashed) return;
    this.crashed = true;
    // Degrade cleanly: end the run (gameover overlay) instead of a permanent freeze.
    try {
      if (this.phase === "playing") this.lose();
    } catch (loseError) {
      console.error("[RunScene] lose() falló durante el manejo del crash:", loseError);
      this.phase = "gameover";
      this.audio.stop();
    }
  }

  private createLayers() {
    this.ground = this.add.tileSprite(0, 0, GAME_WIDTH, GAME_HEIGHT, AssetKeys.terrain.dirt).setOrigin(0).setAlpha(0.62).setScrollFactor(0);
    this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.35).setOrigin(0).setScrollFactor(0);
    this.layers = {
      terrain: this.add.layer(),
      shadows: this.add.layer(),
      obstacles: this.add.layer(),
      pickups: this.add.layer(),
      actors: this.add.layer(),
      fog: this.add.layer(),
      particles: this.add.layer(),
      debug: this.add.layer()
    };
    this.layers.terrain.setDepth(0);
    this.layers.shadows.setDepth(10);
    this.layers.obstacles.setDepth(20);
    this.layers.fog.setDepth(18);
    this.layers.pickups.setDepth(30);
    this.layers.particles.setDepth(34);
    this.layers.actors.setDepth(40);
    this.layers.debug.setDepth(100);
    this.dangerBorder = this.add.graphics().setAlpha(0).setScrollFactor(0);
    this.drawDangerBorder();
    this.createVignette();
  }

  private drawDangerBorder() {
    this.dangerBorder.clear();
    this.dangerBorder.lineStyle(3, 0x781813, 0.5);
    this.dangerBorder.strokeRect(10, 44, GAME_WIDTH - 20, GAME_HEIGHT - 88);
    this.dangerBorder.fillStyle(0x5d0d0d, 0.16);
    this.dangerBorder.fillRect(0, 0, GAME_WIDTH, 76);
    this.dangerBorder.fillRect(0, GAME_HEIGHT - 118, GAME_WIDTH, 118);
    this.dangerBorder.fillRect(0, 0, 42, GAME_HEIGHT);
    this.dangerBorder.fillRect(GAME_WIDTH - 42, 0, 42, GAME_HEIGHT);
  }

  private createVignette() {
    this.vignette = this.add.graphics();
    this.vignette.fillStyle(0x000000, 0.16);
    this.vignette.fillRect(0, 0, GAME_WIDTH, 64);
    this.vignette.setScrollFactor(0);
  }

  private async startRun() {
    this.phase = "playing";
    this.crashed = false;
    this.map.reset();
    this.map.clearSpawnArea(GAME_WIDTH * 0.5, PLAYER_TARGET_Y, 190);
    this.player.reset();
    this.distance = 0;
    this.score.reset();
    this.songEnergy.reset();
    this.songCollectibles.reset();
    this.wolfStamina.reset();
    this.movementFeel = this.wolfStamina.currentFeel;
    this.worldSpeed = 220;
    this.rearThreat = 0;
    this.noForwardTime = 0;
    this.capturePressureTime = 0;
    this.danger = 0;
    this.hitRegisterCooldown = 0;
    this.cameraScrollY = 0;
    this.cameraTargetScrollY = 0;
    this.cameraScrollDeltaY = 0;
    this.ground.tilePositionY = 0;
    this.cameras.main.setScroll(0, 0);
    this.clearMapVisuals();
    this.worldLife.reset();
    this.energyFeedback.reset();
    this.spiritPressure.reset();
    this.championPulse.reset();
    this.rhythmReward.reset();
    this.runRewards.reset();
    this.storyBeats.reset();
    this.microtext.reset();
    this.game.events.emit("ui:hide");
    this.audio.setMusicEnergy(this.songEnergy.ratio, true);
    await this.audio.start();
    this.audio.setMusicEnergy(this.songEnergy.ratio, true);
  }

  private togglePause() {
    if (this.phase === "playing") {
      this.phase = "paused";
      this.audio.pause();
      // Logical pause only freezes gameplay; the scene loop keeps advancing the
      // wolf sprite animation and any in-flight tweens. Stop both explicitly so
      // the wolf goes fully still (anims pause/resume at the current frame, no
      // restart) and pickup/label tweens hold until resume.
      this.player.setVisualPaused(true);
      this.tweens.pauseAll();
      this.game.events.emit("hud:pause", true);
    } else if (this.phase === "paused") {
      this.phase = "playing";
      this.audio.resume();
      this.player.setVisualPaused(false);
      this.tweens.resumeAll();
      this.game.events.emit("hud:pause", false);
    }
  }

  // Undo the visual freeze that togglePause() applies (anim + tweens) without
  // resuming audio/phase. Used before restarting or returning to start from the
  // pause menu, where audio is paused-not-stopped and the wolf anim is frozen —
  // skipping this would leave the new run with a frozen wolf and/or double audio.
  private clearPauseFreeze() {
    this.player.setVisualPaused(false);
    this.tweens.resumeAll();
    this.game.events.emit("hud:pause", false);
  }

  private restartFromPause() {
    this.clearPauseFreeze();
    this.audio.stop();
    void this.startRun();
  }

  private returnToStart() {
    this.phase = "start";
    this.audio.stop();
    // Show start first so it becomes the active panel; then clearPauseFreeze's
    // hud:pause(false) only resets the HUD button and won't fade the overlay out
    // (the pause-panel hide guard checks activePanel === "pause").
    this.game.events.emit("ui:show-start");
    this.clearPauseFreeze();
  }

  private toggleDebug() {
    this.debug = !this.debug;
    this.layers.debug.setVisible(this.debug);
  }

  private updatePlaying(dt: number) {
    this.audio.update(dt);
    const songElapsedTime = this.audio.getSongTime();
    if (songElapsedTime >= GAME_DURATION) {
      this.win();
      return;
    }

    this.songEnergy.update(dt);
    this.audio.setMusicEnergy(this.songEnergy.ratio);
    this.hitRegisterCooldown = Math.max(0, this.hitRegisterCooldown - dt);

    const terrain = this.map.getTerrainAt(this.player.x, this.player.y) as TerrainType;
    this.player.lastTerrain = terrain;
    this.movementFeel = this.wolfStamina.update({
      dt,
      elapsed: songElapsedTime,
      progress: this.audio.progress,
      danger: this.danger,
      rearThreat: this.rearThreat,
      musicIntensity: this.audio.segment.intensity,
      threatDistancePx: this.getThreatDistancePx(),
      hasForwardIntent: this.hasForwardIntent(),
      isChampion: this.player.isChampion
    });
    this.player.update(dt, TERRAIN[terrain].speed, this.rearThreat, this.movementFeel);
    this.worldSpeed = Phaser.Math.Linear(this.worldSpeed, this.calculateWorldSpeed(), Math.min(1, dt * 1.8));
    this.distance += (this.worldSpeed * dt) / 5.6;
    this.map.update(dt, this.worldSpeed, songElapsedTime);
    this.songCollectibles.update({
      songElapsedTime,
      worldSpeed: this.worldSpeed,
      pickupLineY: this.player.hitboxCenterY - 40,
      map: this.map
    });
    this.rhythmReward.updateMagnet(dt, this.map.getAllPickups(), {
      x: this.player.x,
      y: this.player.hitboxCenterY
    });

    const body = {
      x: this.player.x,
      y: this.player.y,
      radius: this.player.pickupRadius,
      width: this.player.hitboxWidth,
      height: this.player.hitboxHeight,
      offsetY: this.player.hitboxOffsetY
    };
    const collisions = this.collision.resolveObstacles(body, this.map.getAllObstacles());
    this.player.x = Phaser.Math.Clamp(body.x, 58, GAME_WIDTH - 58);
    this.player.y = Phaser.Math.Clamp(body.y, GAME_HEIGHT * 0.32, GAME_HEIGHT * 0.94);
    this.player.container.setPosition(this.player.x, this.player.y);

    this.updateThreat(dt, terrain, collisions);
    this.player.pullTowardGhostWall(dt, this.rearThreat);
    this.collectPickups();
    this.score.update(dt, this.danger, this.player.isChampion, this.songEnergy.ratio);
    this.updateRunRewards();
    this.syncMapVisuals();
    this.ghosts.update(songElapsedTime, this.audio.segment.intensity, this.worldLife);
    this.updateDanger(dt);
    const wolfSpeed = Math.hypot(this.player.vx, this.worldSpeed - this.player.vy);
    const wolfSpeedRatio = Phaser.Math.Clamp(wolfSpeed / Math.max(1, this.movementFeel.maxSpeed), 0, 1.2);
    this.audio.updateEmotion({
      dt,
      elapsed: songElapsedTime,
      danger: this.danger,
      rearThreat: this.rearThreat,
      speed: wolfSpeed,
      speedRatio: wolfSpeedRatio,
      isChampion: this.player.isChampion
    });
    this.updateCamera(dt);
    this.worldLife.update(dt, songElapsedTime, this.worldSpeed, this.cameraScrollDeltaY, {
      x: this.player.x,
      y: this.player.y,
      vx: this.player.vx,
      vy: this.player.vy
    });
    const currentBiome = this.map.getCurrentBiome(this.player.y);
    const microtext = this.microtext.update({
      dt,
      progress: this.audio.progress,
      musicEnergy: this.songEnergy.ratio,
      rearThreat: this.rearThreat,
      danger: this.danger,
      biome: currentBiome
    });
    if (microtext) this.emitMicrotext(microtext);
    const storyHint = this.storyBeats.update(dt, currentBiome, this.audio.progress, this.spiritPressure, this.championPulse);
    if (!microtext && storyHint) this.game.events.emit("story:hint", { text: storyHint, durationMs: 950 });
    this.spiritPressure.update(dt, this.rearThreat, this.danger, currentBiome, songElapsedTime);
    this.championPulse.update(
      dt,
      { x: this.player.x, y: this.player.y },
      this.movementFeel.state === "sprint" || this.player.isChampion,
      this.audio.progress,
      this.danger,
      songElapsedTime
    );
    
    // Y-depth sorting for all elements in the actors layer (player, ghosts, tall obstacles)
    this.layers.actors.list.forEach((child: any) => {
      if (child.y !== undefined) {
        child.setDepth(child.y);
      }
    });
    
    this.updateDebug();

    if (this.capturePressureTime > 0.75 && this.ghosts.isCaught(this.player, this.rearThreat)) {
      this.lose();
    }
  }

  private updateAmbient(dt: number) {
    if (this.phase === "playing") {
      const obstacleVisualDy = this.worldSpeed * dt - this.cameraScrollDeltaY;
      this.ground.tilePositionY -= obstacleVisualDy;
      this.cameraScrollDeltaY = 0;
    }
    this.dangerBorder.setAlpha(this.phase === "playing" ? Phaser.Math.Clamp((this.danger - 0.48) * 0.6, 0, 0.34) : 0);
  }

  private updateThreat(dt: number, terrain: TerrainType, collisions: number) {
    const forwardIntent = this.hasForwardIntent();
    this.noForwardTime = forwardIntent ? Math.max(0, this.noForwardTime - dt * 3) : this.noForwardTime + dt;
    const slowTerrain = Phaser.Math.Clamp((0.9 - TERRAIN[terrain].speed) / 0.26, 0, 1);
    const noForward = Phaser.Math.Clamp((this.noForwardTime - 0.8) / 2.8, 0, 1) * 0.78;
    const pushedBack = Phaser.Math.Clamp((this.player.y - PLAYER_TARGET_Y) / (GAME_HEIGHT * 0.2), 0, 1) * 0.45;
    const playerBottom = this.player.hitboxCenterY + this.player.hitboxHeight * 0.5;
    const ghostProximity = Phaser.Math.Clamp((playerBottom - (GHOST_FRONT_LINE - 96)) / 128, 0, 1) * 0.85;
    const target = Phaser.Math.Clamp(noForward + collisions * 0.72 + pushedBack + ghostProximity + slowTerrain * 0.12, 0, 1);
    this.rearThreat = Phaser.Math.Linear(this.rearThreat, target, target > this.rearThreat ? 0.22 : 0.05);
    this.capturePressureTime = this.rearThreat > 0.72 ? this.capturePressureTime + dt : Math.max(0, this.capturePressureTime - dt * 1.5);
    if (collisions > 0) {
      if (this.hitRegisterCooldown <= 0) {
        this.score.addHit();
        this.hitRegisterCooldown = 0.48;
      }
      this.emitMicrotext(this.microtext.onPlayerHit());
    }
  }

  private emitMicrotext(message: MicrotextMessage | null) {
    if (!message) return;
    this.game.events.emit("story:hint", message);
  }

  private hasForwardIntent() {
    const keyboard = this.input.keyboard;
    const keyForward = Boolean(keyboard?.addKey("W").isDown || keyboard?.addKey("UP").isDown);
    const keyLateral = Boolean(keyboard?.addKey("A").isDown || keyboard?.addKey("LEFT").isDown || keyboard?.addKey("D").isDown || keyboard?.addKey("RIGHT").isDown);
    const pointer = this.input.activePointer;
    const pointerIntent = pointer.isDown && Math.hypot(pointer.x - this.player.x, pointer.y - this.player.y) > GAME_WIDTH * 0.035;
    const lateralIntent = Math.abs(this.player.vx) > 24;
    return keyForward || keyLateral || pointerIntent || lateralIntent || this.player.vy < -30;
  }

  private calculateWorldSpeed() {
    const progress = this.audio.progress;
    const realismMultiplier = this.movementFeel?.worldSpeedMultiplier ?? 1;
    return Phaser.Math.Clamp((220 + Math.pow(progress, 1.16) * 430) * realismMultiplier * (this.player.isChampion ? 1.08 : 1), 150, 740);
  }

  private getThreatDistancePx() {
    if (!this.player) return GHOST_FRONT_LINE;
    const playerBottom = this.player.hitboxCenterY + this.player.hitboxHeight * 0.5;
    return Math.max(0, GHOST_FRONT_LINE - playerBottom);
  }

  private collectPickups() {
    const collected = this.collision.collectPickups(
      { x: this.player.x, y: this.player.hitboxCenterY, radius: this.player.pickupRadius },
      this.map.getAllPickups()
    );
    collected.forEach((pickup) => {
      const championReady = this.player.addEnergy();
      this.songCollectibles.recordPickup(pickup, this.audio.getSongTime());
      const timing = this.rhythmReward.judgePickup(pickup, this.audio.getSongTime(), { x: this.player.x, y: this.player.y });
      this.score.addPickup(timing.scoreValue, timing, {
        songSyncEventId: pickup.songSyncEventId,
        intensity: pickup.intensity,
        targetCounterTime: pickup.targetCounterTime
      });
      this.songEnergy.addPickupBoost(pickup.energyRestoreMultiplier ?? 1);
      this.audio.setMusicEnergy(this.songEnergy.ratio);
      this.audio.playPickup(this.player.energy);
      this.energyFeedback.playPickupFeedback(pickup, { x: this.player.x, y: this.player.y });
      this.emitMicrotext(this.microtext.onPickup(pickup));
      if (
        pickup.syncType === "champion_core" ||
        pickup.syncType === "champion_burst" ||
        pickup.syncType === "first_boost_core" ||
        pickup.syncType === "final_champion_core" ||
        pickup.syncType === "pack_echo_core"
      ) {
        this.championPulse.trigger(0.72);
      }
      if (pickup.syncType === "spirit_energy" || pickup.syncType === "pressure_core") this.spiritPressure.pulse(0.82);
      if (championReady) {
        this.score.addChampionActivation();
        this.audio.playChampionCharge();
        this.emitMicrotext(this.microtext.onChampionBoostStart());
      }
      const visual = this.pickupSprites.get(pickup.id);
      this.tweens.add({
        targets: visual?.container,
        alpha: 0,
        scale: 1.55,
        duration: 180,
        onComplete: () => this.removePickup(pickup.id)
      });
    });
  }

  private updateDanger(dt: number) {
    const rearGap = Math.max(0, GHOST_FRONT_LINE - this.player.y);
    const target = Phaser.Math.Clamp(this.rearThreat * 0.72 + (260 - rearGap) / 360, 0, 1);
    this.danger = Phaser.Math.Linear(this.danger, target, 0.08);
    this.shakeCooldown -= dt;
    if (this.danger > 0.64 && this.audio.beat > 0.35 && this.shakeCooldown <= 0) {
      this.cameras.main.shake(60, 0.0013);
      this.shakeCooldown = 0.35;
    }
  }

  private updateRunRewards() {
    const rhythm = this.rhythmReward.getState();
    const score = this.score.getStats();
    const events = this.runRewards.update({
      combo: rhythm.combo,
      perfectPickups: rhythm.perfectPickups,
      songAliveSeconds: score.songAliveSeconds,
      distance: this.distance,
      musicEnergy: this.songEnergy.ratio
    });
    events.forEach((event) => {
      this.rhythmReward.showFloatingLabel(event.text, { x: this.player.x, y: this.player.y }, event.tone);
      this.game.events.emit("ui:toast", event);
    });
  }

  private updateCamera(dt: number) {
    const desired = Phaser.Math.Clamp(this.player.y - GAME_HEIGHT * 0.68, -GAME_HEIGHT * 0.05, GAME_HEIGHT * 0.08);
    const deadZone = 7;
    const delta = desired - this.cameraTargetScrollY;
    if (Math.abs(delta) > deadZone) {
      this.cameraTargetScrollY = desired - Math.sign(delta) * deadZone;
    }
    const previousScrollY = this.cameraScrollY;
    this.cameraScrollY = Phaser.Math.Linear(this.cameraScrollY, this.cameraTargetScrollY, Math.min(1, dt * WOLF_FEEL_CONFIG.cameraFollowLerp));
    this.cameraScrollY = this.worldLife.applyCameraBreathing(this.cameraScrollY, this.audio.elapsed);
    this.cameraScrollDeltaY = this.cameraScrollY - previousScrollY;
    this.cameras.main.setScroll(0, this.cameraScrollY);
  }

  private syncMapVisuals() {
    const obstacles = this.map.getAllObstacles();
    const pickups = this.map.getAllPickups();
    const obstacleIds = new Set(obstacles.map((item) => item.id));
    const pickupIds = new Set(pickups.map((item) => item.id));

    obstacles.forEach((obstacle) => this.syncObstacle(obstacle));
    pickups.forEach((pickup) => this.syncPickup(pickup));
    this.obstacleSprites.forEach((visual, id) => {
      if (!obstacleIds.has(id)) {
        visual.sprite.destroy();
        visual.shadow.destroy();
        this.obstacleSprites.delete(id);
      }
    });
    this.pickupSprites.forEach((visual, id) => {
      if (!pickupIds.has(id)) this.removePickup(id);
    });
  }

  private syncObstacle(obstacle: ObstacleData) {
    let visual = this.obstacleSprites.get(obstacle.id);
    const isFlat = obstacle.key.includes("puddle") || 
                   obstacle.key.includes("moss") || 
                   obstacle.key.includes("cracks") || 
                   obstacle.key.includes("mark") || 
                   obstacle.key.includes("grass") || 
                   obstacle.key.includes("bones") || 
                   obstacle.key.includes("skull");
    
    if (!visual) {
      const shadowAlpha = isFlat ? 0 : (obstacle.blocking ? 0.38 : 0.08);
      const shadow = this.add.ellipse(obstacle.x, obstacle.y + obstacle.height * 0.2, obstacle.width * 0.6, obstacle.height * 0.22, 0x000000, shadowAlpha);
      const sprite = this.add.image(obstacle.x, obstacle.y, obstacle.key);
      this.layers.shadows.add(shadow);
      
      const needsYSort = obstacle.ySort || 
                         obstacle.blocking || 
                         obstacle.key.includes("tree") || 
                         obstacle.key.includes("ruin") || 
                         obstacle.key.includes("log") || 
                         obstacle.key.includes("root") ||
                         obstacle.key.includes("stump");
      
      if (needsYSort) {
        this.layers.actors.add(sprite);
      } else {
        this.layers.obstacles.add(sprite);
      }
      
      visual = { sprite, shadow };
      this.obstacleSprites.set(obstacle.id, visual);
    }
    visual.shadow.setPosition(obstacle.x, obstacle.y + obstacle.height * 0.22).setSize(obstacle.width * 0.6, obstacle.height * 0.22);
    visual.shadow.setAlpha(isFlat ? 0 : (obstacle.blocking ? 0.38 : 0.08));
    visual.sprite.setPosition(obstacle.x, obstacle.y).setRotation(obstacle.rotation).setDisplaySize(obstacle.width, obstacle.height);
    visual.sprite.setAlpha(obstacle.decorative ? (obstacle.story ? 0.48 : 0.32) : 0.86);
    
    if (obstacle.decorative) {
      const isGreenDeco = obstacle.key.includes("moss") || obstacle.key.includes("cracks") || obstacle.key.includes("mushrooms") || obstacle.key.includes("mark");
      visual.sprite.setTint(isGreenDeco ? 0xa8d975 : obstacle.story ? 0x75836f : 0x5a6954);
    } else {
      visual.sprite.setTint(0x8f9888);
    }
    this.worldLife.applyObstacleLife(obstacle, visual, this.audio.elapsed);
  }

  private syncPickup(pickup: PickupData) {
    let visual = this.pickupSprites.get(pickup.id);
    if (!visual) {
      visual = this.energyFeedback.createPickupVisual(pickup);
      this.pickupSprites.set(pickup.id, visual);
    }
    this.energyFeedback.updatePickupVisual(pickup, visual, this.audio.elapsed);
    this.worldLife.applyPickupGlow(pickup, visual, this.audio.elapsed);
  }

  private removePickup(id: string) {
    this.pickupSprites.get(id)?.container.destroy();
    this.pickupSprites.delete(id);
  }

  private updateDebug() {
    this.debugGraphics.clear();
    if (!this.debug) return;
    this.debugGraphics.lineStyle(2, 0xb7ff00, 0.72);
    this.map.modules.forEach((module) => {
      this.debugGraphics.beginPath();
      module.safePath.forEach((point, index) => {
        if (index === 0) this.debugGraphics.moveTo(point.x, point.y);
        else this.debugGraphics.lineTo(point.x, point.y);
      });
      this.debugGraphics.strokePath();
      module.obstacles.forEach((obstacle) => {
        if (!obstacle.blocking) return;
        this.debugGraphics.strokeRect(obstacle.x - obstacle.width * 0.28, obstacle.y - obstacle.height * 0.24, obstacle.width * 0.56, obstacle.height * 0.48);
      });
    });
    this.debugGraphics.lineStyle(2, 0xdfff2f, 0.58);
    this.debugGraphics.strokeRect(
      this.player.x - this.player.hitboxWidth * 0.5,
      this.player.hitboxCenterY - this.player.hitboxHeight * 0.5,
      this.player.hitboxWidth,
      this.player.hitboxHeight
    );
  }

  private clearMapVisuals() {
    this.obstacleSprites.forEach((item) => {
      item.sprite.destroy();
      item.shadow.destroy();
    });
    this.pickupSprites.forEach((item) => item.container.destroy());
    this.obstacleSprites.clear();
    this.pickupSprites.clear();
  }

  private lose() {
    this.phase = "gameover";
    // Freeze the wolf on its current frame (the run is over). Only the wolf anim,
    // not tweens.pauseAll() — the world tweens are hidden behind the overlay.
    this.player.setVisualPaused(true);
    const result = this.score.finalize(this.audio.elapsed, this.distance, this.phase, this.songEnergy.ratio);
    this.audio.stop();
    this.audio.playLose();
    this.game.events.emit("ui:gameover", result);
  }

  private win() {
    this.phase = "victory";
    // Same as lose(): hold the wolf still on the victory screen (wolf anim only).
    this.player.setVisualPaused(true);
    const result = this.score.finalize(this.audio.elapsed, this.distance, this.phase, this.songEnergy.ratio);
    this.audio.stop();
    this.audio.playWin();
    this.game.events.emit("ui:victory", result);
  }

  private emitHud() {
    const rhythm = this.rhythmReward?.getState() ?? { combo: 0, multiplier: 1, bestCombo: 0 };
    this.game.events.emit("hud:update", {
      elapsed: this.audio.elapsed,
      remaining: GAME_DURATION - this.audio.elapsed,
      danger: this.danger,
      energy: this.player?.energy ?? 0,
      musicEnergy: this.songEnergy?.ratio ?? 0,
      musicActive: this.songEnergy?.isAudible ?? false,
      score: this.score.calculate(this.audio?.elapsed ?? 0, this.distance, this.phase, this.songEnergy?.ratio ?? 0).total,
      combo: rhythm.combo,
      multiplier: rhythm.multiplier,
      bestCombo: rhythm.bestCombo,
      wolfState: this.movementFeel?.state ?? "longChase",
      wolfStamina: this.movementFeel?.stamina ?? 100,
      phase: this.phase,
      debug: this.debug,
      terrain: this.player?.lastTerrain ?? "dirt",
      fps: this.fps
    });
  }
}
